// All selection values stored as English keys. Raw data (names, addresses, numbers) stored as entered. Display layer handles localization.
import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from "react";
import { mvpApi } from "../../services/api";
import {
  deriveDocumentChecklistHints,
  deriveUploadFilenamesFromSessionSummary,
  mergeAsylumStateFromSessionSummary,
  mergeFamilyStateFromSessionSummary,
  inferWorkTypeFromUploadedEmploymentDocs,
  mergeEmploymentStateFromSessionSummary,
  mergeStayStateFromSessionSummary,
  mergeCriminalStateFromSessionSummary,
  mergeOtherDocumentsFromSessionSummary,
  mergeVulnerabilityReportFromSessionSummary,
  type DocumentChecklistHints,
  type DocumentSectionAckKey,
} from "../utils/deriveDocumentChecklistHints";
import { parseStoredFloorDoor } from "../mocks/applicantData";
import { splitDialNationalFromStored } from "../utils/splitDialNational";
import {
  validateDateValueParts,
  validateOptionalDateParts,
  validateOptionalMonthYear,
} from "../utils/datePartsValidation";
import { useLang } from "./LanguageContext";
import { showPopupMessage } from "../../utils/showPopup";

type CivilStatusFormSlug = "single" | "married" | "widowed" | "divorced" | "separated" | "";

function normalizeCivilStatusSlug(raw: unknown): CivilStatusFormSlug {
  const value = String(raw ?? "").trim().toLowerCase();
  if (!value) return "";
  const ascii = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x00-\x7F]/g, "")
    .trim()
    .toLowerCase();

  if (
    value === "single" ||
    value === "soltero" ||
    value === "soltera" ||
    value === "s" ||
    ascii === "soltero" ||
    ascii === "soltera"
  ) {
    return "single";
  }
  if (
    value === "married" ||
    value === "casado" ||
    value === "casada" ||
    value === "m" ||
    value === "c" ||
    ascii === "casado" ||
    ascii === "casada"
  ) {
    return "married";
  }
  if (value.startsWith("divorc")) return "divorced";
  if (value.startsWith("separ") || value === "sp" || value === "p") return "separated";
  if (
    value === "widow" ||
    value === "widowed" ||
    value === "viudo" ||
    value === "viuda" ||
    value === "w" ||
    value === "v" ||
    ascii === "viudo" ||
    ascii === "viuda"
  ) {
    return "widowed";
  }
  if (value === "single" || value === "married" || value === "widowed" || value === "divorced" || value === "separated") {
    return value;
  }
  return "";
}

export interface DateValue {
  day: string;
  month: string;
  year: string;
}

export interface SpouseDetails {
  firstName: string;
  primerApellido: string;
  segundoApellido: string;
  dob: DateValue;
  sex: "male" | "female" | "undetermined" | "";
  nationality: string;
  hasLegalStatus: "yes" | "no" | "";
  nieOrPassport: string;
}

export interface ChildEntry {
  id: string;
  firstName: string;
  primerApellido: string;
  segundoApellido: string;
  dob: DateValue;
  sex: "male" | "female" | "undetermined" | "";
  nationality: string;
  livesInSpain: "yes" | "no" | "";
  inSchool: "yes" | "no" | "";
}

export interface DependentParent {
  id: string;
  firstName: string;
  primerApellido: string;
  dob: DateValue;
  sex: "male" | "female" | "undetermined" | "";
  nationality: string;
}

export interface MonthYearValue {
  month: string;
  year: string;
}

export interface StayDocument {
  id: string;
  filename: string;
  fileSize: string;
  type: string;
  /** Optional free-text subtype, used when `type` is `Other`. */
  description?: string;
  month: string;
  year: string;
  /** Mongo document id after upload; used for DELETE /documents/... */
  documentId?: string;
}

export interface CriminalCertificate {
  country: string;
  filename: string;
  status: "pending" | "uploaded" | "notAvailable";
  /** Mongo document id after successful POST /upload/{session_id}. */
  documentId?: string;
  /** Local-only proof state for "requested but not received" criminal certificates. */
  certRequestedNotReceived?: boolean;
  /** Local-only filename for proof of request. */
  requestProofFile?: string;
}

export interface AsylumDocument {
  id: string;
  category: string;
  filename: string;
  fileSize: string;
  description: string;
  /** Mongo uploaded document id after POST /upload/{session_id}. */
  documentId?: string;
}

export interface EmploymentDocument {
  id: string;
  category: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

export interface FamilyDocument {
  id: string;
  category: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

export interface OtherDocument {
  id: string;
  docType: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

export interface VulnerabilityReport {
  filename: string;
  fileSize: string;
  documentId?: string;
}

export interface ParentNameDetails {
  firstName: string;
  primerApellido: string;
  segundoApellido: string;
}

export interface FormData {
  // Pre-filled (from registration)
  fullName: string;
  phone: string;
  phoneCountryCode: string;
  email: string;
  registrantType: "individual" | "representative" | "";
  advisor: string;
  familyInterest: boolean;

  // Section 1 — Confirm Details
  firstName: string;
  primerApellido: string;
  segundoApellido: string;
  repRelationship: string;
  applicantFirstName: string;
  applicantPrimerApellido: string;
  applicantSegundoApellido: string;
  applicantEmail: string;
  applicantPhone: string;
  /** Dial code for optional applicant phone (representative flow); independent from rep `phoneCountryCode`. */
  applicantPhoneCountryCode: string;
  /** Representative flow: whether account contact was moved to applicant block. */
  contactAssignedTo: "rep" | "applicant";
  /** After move to applicant: rep must verify new mobile via profile OTP before step 1 continue. */
  repPhoneOtpVerified: boolean;

  // Section 2 — Personal Info
  dob: DateValue;
  sex: "male" | "female" | "undetermined" | "";
  nationality: string;
  countryOfBirth: string;
  placeOfBirth: string;

  // Section 3 — Civil & Family
  civilStatus: "single" | "married" | "widowed" | "divorced" | "separated" | "";
  hasSpouse: "yes" | "no" | "";
  spouse: SpouseDetails;
  hasChildren: "yes" | "no" | "";
  children: ChildEntry[];
  hasDependentParents: "yes" | "no" | "";
  dependentParents: DependentParent[];
  hasDependentsInSpain: "yes" | "no" | "";
  fatherFirstName: string;
  fatherApellido1: string;
  fatherApellido2: string;
  fatherUnknown: "yes" | "";
  motherFirstName: string;
  motherApellido1: string;
  motherApellido2: string;
  motherUnknown: "yes" | "";

  // Section 4 — Address in Spain
  street: string;
  streetType: string;
  streetNumber: string;
  floorPiso: string;
  floorDoor: string;
  municipality: string;
  locality: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  residenceSince: MonthYearValue;

  // Section 2e — Eligibility / Situation
  hasValidPermit: "yes" | "no" | "";
  asylumRoutingConfirmed: boolean | null;
  asylumApplicant: "yes" | "no" | "";
  qualifyingGround: "work" | "family" | "vulnerability" | "";
  /** Advisor-set Mongo `prior_asylum_application`; receipt upload only when `after`. */
  priorAsylumApplication: "before" | "after" | "never" | "";
  /** When asylum applicant (before Jan 2026): optional OAR / expediente reference. */
  oarCaseNumber: string;
  /** Source-of-truth scenario selection for asylum applicants. */
  asylumScenario: "1" | "2" | "3" | "4" | "5" | "6" | "";
  /** Source-of-truth route selection for non-asylum applicants. */
  subRoute: "7A" | "7B" | "7C" | "8" | "9" | "10" | "11" | "12" | "";
  /** Extension / renewal basis used by grouped options. */
  prorrogaType: "jobSearch" | "integrationReport" | "illness" | "disability" | "retirement" | "";
  /** When not an asylum applicant: which irregular situation applies. */
  irregularSubRoute: "work" | "family" | "vulnerability" | "";
  /** Follow-up detail when the applicant selects the work sub-route. */
  workHistory: "worked" | "current" | "offer" | "";
  /** Reserved for later matching source-of-truth follow-ups. */
  workType: "employed" | "selfemployed" | "";
  selfEmploymentActivity: string;
  hasCriminalRecord: "yes" | "no" | "";
  previousApplication: "yes" | "no" | "";
  hasPassport: "yes" | "no" | "";
  passportOcrSkipped: "yes" | "";
  requestedCallback: boolean;

  // Section 5 — Situation in Spain
  arrivalDate: DateValue;
  wantsToWork: "yes" | "no" | "";
  workSituation: "employed" | "looking" | "selfemployed" | "";
  nie: string;
  nationalIdNumber: string;
  travelDocumentNumber: string;
  registrationDocumentNumber: string;
  otherDocumentNumber: string;
  nationalIdCountry: string;

  // Section 6 — Identity Document
  docType: "passport" | "nationalId" | "travelDoc" | "cedulaInscripcion" | "other" | "";
  docTypeOther: string;
  docFullName: string;
  passportNumber: string;
  docExpiry: DateValue;
  docUpload: string;
  identityUploads: {
    passport: string;
    nationalId: string;
    travelDoc: string;
    cedulaInscripcion: string;
    other: string;
  };
  /** Mongo document id for uploaded passport (session summary / upload response); presigned view. */
  identityPassportDocumentId?: string;

  // Section 7 — Proof of Stay
  // Persona-based uploads
  asylumDocType: string;
  asylumDocUpload: string;
  asylumOptionalExpanded: boolean;
  denialResolutionUpload: string;
  /** Mongo id for prior_denial_resolution (server delete). */
  denialResolutionDocumentId: string;
  priorCriminalRecordUpload: string;
  criminalRecordDocUpload: string;
  countriesOfResidence: string[];
  criminalCertificates: CriminalCertificate[];
  // Typed repeating stay documents
  stayDocuments: StayDocument[];
  // Family documents grouped by document type
  familyDocuments: FamilyDocument[];
  // Employment documents grouped by document type
  employmentDocuments: EmploymentDocument[];
  // Asylum documents grouped by document type
  asylumDocuments: AsylumDocument[];
  // Vulnerability report upload
  vulnerabilityReport: VulnerabilityReport;
  // Additional advisor-requested documents
  otherDocuments: OtherDocument[];

  // Section 8 — Engagement
  noReturnCommitment: boolean;
  noHealthcareCoverage: boolean;
  engagementConsent: boolean;
  dataProcessingConsent: boolean;
  marketingConsent: boolean;
  signature: string;
  questionsRequested: boolean;

  // Legacy compat
  docNumber: string;
  phoneConfirmed: string;
  emailConfirmed: string;
  addressLine1: string;
  addressLine2: string;
  yearsInSpain: string;
  monthsInSpain: string;

  /** True after applicant confirms the proof-of-residence exit modal (Yes). Synced to session collected_fields. */
  proofOfResidenceAcknowledged: boolean;

  /** Per-section exit modal confirmations; synced as `document_section_acknowledged` in session collected_fields. */
  documentSectionAcknowledged: Partial<Record<DocumentSectionAckKey, boolean>>;

  /** Filled after GET /documents/session/:id/summary so checklist matches Mongo across reloads. */
  documentChecklistHints?: DocumentChecklistHints;
  /** From same summary: applicant organizer 'submit for review' / needs-attention (Mongo session). */
  caseNeedsAttentionMarked?: boolean;
  /** ISO timestamp persisted in Mongo when applicant clicked "Sent to Ana". */
  caseNeedsAttentionMarkedAt?: string;
}

function splitFullName(full: string): { first: string; primer: string; segundo: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return { first: "", primer: "", segundo: "" };
  if (parts.length === 1) return { first: parts[0], primer: "", segundo: "" };
  if (parts.length === 2) return { first: parts[0], primer: parts[1], segundo: "" };
  if (parts.length === 3) {
    return {
      first: parts.slice(0, 2).join(" "),
      primer: parts[2],
      segundo: "",
    };
  }
  const primer = parts[parts.length - 2];
  const segundo = parts[parts.length - 1];
  const first = parts.slice(0, parts.length - 2).join(" ");
  return { first, primer, segundo };
}

function splitSurnamesFromLastName(last: string): { primer: string; segundo: string } {
  const parts = String(last || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { primer: "", segundo: "" };
  if (parts.length === 1) return { primer: parts[0], segundo: "" };
  return {
    primer: parts[0],
    segundo: parts.slice(1).join(" "),
  };
}

function composeFullNameParts(first: string, primer: string, segundo: string): string {
  return [first, primer, segundo]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
}

const fullName = "";
const phone = "";
const phoneCountryCode = "+34";
const email = "";
/** Default guide name — matches client reference portal; replace when API provides assigned advisor. */
const advisor = "Ana Travieso";
const registrantType = "" as "individual" | "representative" | "";
const split = splitFullName(fullName);

function normalizeParentFirstNameValue(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return value.replace(/(^|[\s'-])([^\s'-])/g, (_full, prefix: string, char: string) => {
    return `${prefix}${char.toLocaleUpperCase()}`;
  });
}

function normalizeTitleCaseWordStarts(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return value.replace(/(^|[\s'-])([^\s'-])/g, (_full, prefix: string, char: string) => {
    return `${prefix}${char.toLocaleUpperCase()}`;
  });
}

const initialData: FormData = {
  fullName,
  phone,
  phoneCountryCode,
  email,
  registrantType,
  advisor,
  familyInterest: false,

  firstName: split.first,
  primerApellido: split.primer,
  segundoApellido: split.segundo,

  repRelationship: "",
  applicantFirstName: "",
  applicantPrimerApellido: "",
  applicantSegundoApellido: "",
  applicantEmail: "",
  applicantPhone: "",
  applicantPhoneCountryCode: "+34",

  contactAssignedTo: "rep",
  repPhoneOtpVerified: true,

  dob: { day: "", month: "", year: "" },
  sex: "",
  nationality: "",
  countryOfBirth: "",
  placeOfBirth: "",

  civilStatus: "",
  hasSpouse: "",
  spouse: {
    firstName: "",
    primerApellido: "",
    segundoApellido: "",
    dob: { day: "", month: "", year: "" },
    sex: "",
    nationality: "",
    hasLegalStatus: "",
    nieOrPassport: "",
  },
  hasChildren: "",
  children: [],
  hasDependentParents: "",
  dependentParents: [],
  hasDependentsInSpain: "",
  fatherFirstName: "",
  fatherApellido1: "",
  fatherApellido2: "",
  fatherUnknown: "",
  motherFirstName: "",
  motherApellido1: "",
  motherApellido2: "",
  motherUnknown: "",

  street: "",
  streetType: "",
  streetNumber: "",
  floorPiso: "",
  floorDoor: "",
  municipality: "",
  locality: "",
  city: "",
  province: "",
  postalCode: "",
  country: "España",
  residenceSince: { month: "", year: "" },

  hasValidPermit: "",
  asylumRoutingConfirmed: null,
  asylumApplicant: "",
  qualifyingGround: "",
  priorAsylumApplication: "",
  oarCaseNumber: "",
  asylumScenario: "",
  subRoute: "",
  prorrogaType: "",
  irregularSubRoute: "",
  workHistory: "",
  workType: "",
  selfEmploymentActivity: "",
  hasCriminalRecord: "",
  previousApplication: "",
  hasPassport: "",
  passportOcrSkipped: "",
  requestedCallback: false,

  arrivalDate: { day: "", month: "", year: "" },
  wantsToWork: "",
  workSituation: "",
  nie: "",
  nationalIdNumber: "",
  travelDocumentNumber: "",
  registrationDocumentNumber: "",
  otherDocumentNumber: "",
  nationalIdCountry: "",

  docType: "passport",
  docTypeOther: "",
  docFullName: "",
  passportNumber: "",
  docExpiry: { day: "", month: "", year: "" },
  docUpload: "",
  identityUploads: {
    passport: "",
    nationalId: "",
    travelDoc: "",
    cedulaInscripcion: "",
    other: "",
  },

  asylumDocType: "",
  asylumDocUpload: "",
  asylumOptionalExpanded: false,
  denialResolutionUpload: "",
  denialResolutionDocumentId: "",
  priorCriminalRecordUpload: "",
  criminalRecordDocUpload: "",
  countriesOfResidence: [],
  criminalCertificates: [],
  stayDocuments: [],
  familyDocuments: [],
  employmentDocuments: [],
  asylumDocuments: [],
  vulnerabilityReport: { filename: "", fileSize: "" },
  otherDocuments: [],

  noReturnCommitment: false,
  noHealthcareCoverage: false,
  engagementConsent: false,
  dataProcessingConsent: false,
  marketingConsent: false,
  signature: "",
  questionsRequested: false,

  proofOfResidenceAcknowledged: false,
  documentSectionAcknowledged: {},
  caseNeedsAttentionMarkedAt: "",

  // Legacy
  docNumber: "",
  phoneConfirmed: `${phoneCountryCode} ${phone}`,
  emailConfirmed: email,
  addressLine1: "",
  addressLine2: "",
  yearsInSpain: "",
  monthsInSpain: "",
};

type SaveState = "idle" | "saving" | "saved";

const CHINESE_TEXT_RE = /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/;

const CHINESE_BLOCKED_INPUT_FIELDS: ReadonlySet<keyof FormData> = new Set([
  "fullName",
  "firstName",
  "primerApellido",
  "segundoApellido",
  "email",
  "applicantFirstName",
  "applicantPrimerApellido",
  "applicantSegundoApellido",
  "applicantEmail",
  "repRelationship",
  "fatherFirstName",
  "fatherApellido1",
  "fatherApellido2",
  "motherFirstName",
  "motherApellido1",
  "motherApellido2",
  "street",
  "streetNumber",
  "floorPiso",
  "floorDoor",
  "city",
  "postalCode",
  "country",
  "placeOfBirth",
  "docFullName",
  "passportNumber",
  "nationalIdNumber",
  "travelDocumentNumber",
  "registrationDocumentNumber",
  "otherDocumentNumber",
  "nationalIdCountry",
  "selfEmploymentActivity",
  "oarCaseNumber",
]);

function containsChineseChars(value: string): boolean {
  return CHINESE_TEXT_RE.test(value);
}

function getChineseInputBlockedMessage(lang: string): string {
  switch (lang) {
    case "es":
      return "No puedes escribir en chino. Por favor, escribe en español.";
    case "fr":
      return "Vous ne pouvez pas saisir en chinois. Veuillez écrire en espagnol.";
    case "ar":
      return "لا يمكنك إدخال نص باللغة الصينية. يُرجى الكتابة بالإسبانية.";
    case "zh":
      return "不能输入中文。请用西班牙语填写。";
    case "en":
    default:
      return "You cannot input in Chinese. Please type in Spanish.";
  }
}

interface FormContextValue {
  data: FormData;
  update: (field: keyof FormData, value: unknown) => void;
  saveState: SaveState;
  currentStep: number;
  totalSteps: number;
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: number) => void;
  consentAccepted: boolean;
  setConsentAccepted: (v: boolean) => void;
  helpRequest: string | null;
  requestHelp: (field: string) => void;
  clearHelpRequest: () => void;
  /** Mongo case session id (UNIFIED chat session) for /api/upload — set after startSession. */
  caseSessionId: string | null;
  /** Ensures a case session exists; returns session_id for uploads. */
  ensureCaseSession: () => Promise<string | null>;
}

const FormContext = createContext<FormContextValue | null>(null);

const TOTAL_STEPS = 4;
const APPLICANT_DRAFT_STORAGE_KEY_PREFIX = "mvp_applicant_autosave_draft_v2";
const APPLICANT_DRAFT_STORAGE_KEY_LEGACY = "mvp_applicant_autosave_draft_v1";

type DraftData = Partial<FormData>;

/**
 * Address fields from GET /auth/me: persisted in Mongo as domicilio, numero_domicilio, piso, puerta,
 * localidad, provincia, codigo_postal, pais_residencia, residente_desde — returned as snake_case on UserOut.
 * Accept camelCase fallbacks for older clients.
 */
function pickMeAddressString(user: Record<string, unknown>, snake: string, camel: string): string {
  const a = user[snake];
  if (a != null && String(a).trim() !== "") return String(a).trim();
  const b = user[camel];
  if (b != null && String(b).trim() !== "") return String(b).trim();
  return "";
}

function toDateValueFromApi(raw: unknown): DateValue {
  if (!raw) return { day: "", month: "", year: "" };
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const day = String((raw as { day?: unknown }).day ?? "").trim();
    const month = String((raw as { month?: unknown }).month ?? "").trim();
    const year = String((raw as { year?: unknown }).year ?? "").trim();
    if (day || month || year) {
      return {
        day: day.padStart(2, "0"),
        month: month.padStart(2, "0"),
        year,
      };
    }
  }
  const value = String(raw).trim();
  if (!value) return { day: "", month: "", year: "" };
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/);
  if (isoMatch) {
    return {
      day: isoMatch[3],
      month: isoMatch[2],
      year: isoMatch[1],
    };
  }
  const dmyMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyMatch) {
    return {
      day: dmyMatch[1].padStart(2, "0"),
      month: dmyMatch[2].padStart(2, "0"),
      year: dmyMatch[3],
    };
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return { day: "", month: "", year: "" };
  return {
    day: String(dt.getUTCDate()).padStart(2, "0"),
    month: String(dt.getUTCMonth() + 1).padStart(2, "0"),
    year: String(dt.getUTCFullYear()),
  };
}

function normalizeSpouseFromApi(raw: unknown, fallback: SpouseDetails): SpouseDetails {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return fallback;
  const spouse = raw as Record<string, unknown>;
  const hasLegalStatus =
    spouse.has_legal_status === true || spouse.hasLegalStatus === true
      ? "yes"
      : spouse.has_legal_status === false || spouse.hasLegalStatus === false
        ? "no"
        : fallback.hasLegalStatus;
  return {
    firstName: String(spouse.first_name ?? spouse.firstName ?? "").trim() || fallback.firstName,
    primerApellido: String(spouse.primer_apellido ?? spouse.primerApellido ?? "").trim() || fallback.primerApellido,
    segundoApellido:
      String(spouse.segundo_apellido ?? spouse.segundoApellido ?? "").trim() || fallback.segundoApellido,
    dob:
      spouse.date_of_birth || spouse.dateOfBirth || spouse.dob
        ? toDateValueFromApi(spouse.date_of_birth ?? spouse.dateOfBirth ?? spouse.dob)
        : fallback.dob,
    sex: (String(spouse.sex ?? "").trim() as SpouseDetails["sex"]) || fallback.sex,
    nationality: String(spouse.nationality ?? "").trim() || fallback.nationality,
    hasLegalStatus,
    nieOrPassport:
      String(spouse.nie_or_passport ?? spouse.nieOrPassport ?? "").trim() || fallback.nieOrPassport,
  };
}

function normalizeChildrenFromApi(raw: unknown): ChildEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === "object" && !Array.isArray(entry))
    .map((entry, index) => {
      const child = entry as Record<string, unknown>;
      const livesInSpain =
        child.lives_in_spain === true || child.livesInSpain === true
          ? "yes"
          : child.lives_in_spain === false || child.livesInSpain === false
            ? "no"
            : (String(child.lives_in_spain ?? child.livesInSpain ?? "").trim() as ChildEntry["livesInSpain"]);
      const inSchool =
        child.in_school === true || child.inSchool === true
          ? "yes"
          : child.in_school === false || child.inSchool === false
            ? "no"
            : (String(child.in_school ?? child.inSchool ?? "").trim() as ChildEntry["inSchool"]);
      return {
        id: String(child.id ?? `child-${index + 1}`).trim() || `child-${index + 1}`,
        firstName: String(child.first_name ?? child.firstName ?? "").trim(),
        primerApellido: String(child.primer_apellido ?? child.primerApellido ?? "").trim(),
        segundoApellido: String(child.segundo_apellido ?? child.segundoApellido ?? "").trim(),
        dob: toDateValueFromApi(child.date_of_birth ?? child.dateOfBirth ?? child.dob),
        sex: (String(child.sex ?? "").trim() as ChildEntry["sex"]) || "",
        nationality: String(child.nationality ?? "").trim(),
        livesInSpain: livesInSpain === "yes" || livesInSpain === "no" ? livesInSpain : "",
        inSchool: inSchool === "yes" || inSchool === "no" ? inSchool : "",
      };
    });
}

function normalizeDependentParentsFromApi(raw: unknown): DependentParent[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === "object" && !Array.isArray(entry))
    .map((entry, index) => {
      const parent = entry as Record<string, unknown>;
      return {
        id: String(parent.id ?? `parent-${index + 1}`).trim() || `parent-${index + 1}`,
        firstName: String(parent.first_name ?? parent.firstName ?? "").trim(),
        primerApellido: String(parent.primer_apellido ?? parent.primerApellido ?? "").trim(),
        dob: toDateValueFromApi(parent.date_of_birth ?? parent.dateOfBirth ?? parent.dob),
        sex: (String(parent.sex ?? "").trim() as DependentParent["sex"]) || "",
        nationality: String(parent.nationality ?? "").trim(),
      };
    });
}

function readApplicantDraft(storageKey: string): DraftData | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed as DraftData;
  } catch {
    return null;
  }
}

function writeApplicantDraft(storageKey: string, data: FormData): void {
  const hasDependentsInSpain =
    data.hasChildren === "yes" || data.hasDependentParents === "yes"
      ? "yes"
      : data.hasChildren === "no" && data.hasDependentParents === "no"
        ? "no"
        : "";

  const out: DraftData = {
    // Step 2e + situation/date (exclude section 1 confirm-details fields by design)
    hasValidPermit: data.hasValidPermit,
    asylumRoutingConfirmed: data.asylumRoutingConfirmed,
    asylumApplicant: data.asylumApplicant,
    qualifyingGround: data.qualifyingGround,
    priorAsylumApplication: data.priorAsylumApplication,
    oarCaseNumber: data.oarCaseNumber,
    asylumScenario: data.asylumScenario,
    subRoute: data.subRoute,
    prorrogaType: data.prorrogaType,
    irregularSubRoute: data.irregularSubRoute,
    workHistory: data.workHistory,
    workType: data.workType,
    selfEmploymentActivity: data.selfEmploymentActivity,
    hasCriminalRecord: data.hasCriminalRecord,
    previousApplication: data.previousApplication,
    hasPassport: data.hasPassport,
    requestedCallback: data.requestedCallback,
    wantsToWork: data.wantsToWork,
    workSituation: data.workSituation,
    nie: data.nie,
    nationalIdNumber: data.nationalIdNumber,
    nationalIdCountry: data.nationalIdCountry,
    // Step 3/4 core details
    docType: data.docType,
    docTypeOther: data.docTypeOther,
    docFullName: data.docFullName,
    passportNumber: data.passportNumber,
    sex: data.sex,
    nationality: data.nationality,
    countryOfBirth: data.countryOfBirth,
    placeOfBirth: data.placeOfBirth,
    // Step 5
    civilStatus: data.civilStatus,
    hasSpouse: data.hasSpouse,
    spouse: data.spouse,
    hasChildren: data.hasChildren,
    children: data.children,
    hasDependentParents: data.hasDependentParents,
    dependentParents: data.dependentParents,
    hasDependentsInSpain,
    fatherFirstName: data.fatherFirstName,
    fatherApellido1: data.fatherApellido1,
    fatherApellido2: data.fatherApellido2,
    fatherUnknown: data.fatherUnknown,
    motherFirstName: data.motherFirstName,
    motherApellido1: data.motherApellido1,
    motherApellido2: data.motherApellido2,
    motherUnknown: data.motherUnknown,
    // Step 6
    street: data.street,
    streetType: data.streetType,
    streetNumber: data.streetNumber,
    floorPiso: data.floorPiso,
    floorDoor: data.floorDoor,
    municipality: data.municipality,
    locality: data.locality,
    city: data.city,
    province: data.province,
    postalCode: data.postalCode,
    country: data.country,
    // NOTE: Document upload state is intentionally NOT persisted in localStorage.
    // It is always fetched fresh from the API (GET /documents/session/:id/summary)
    // on every mount to ensure the organizer reflects the true server state.
    // Step 8
    noReturnCommitment: data.noReturnCommitment,
    noHealthcareCoverage: data.noHealthcareCoverage,
    engagementConsent: data.engagementConsent,
    dataProcessingConsent: data.dataProcessingConsent,
    marketingConsent: data.marketingConsent,
    signature: data.signature,
    questionsRequested: data.questionsRequested,
    proofOfResidenceAcknowledged: data.proofOfResidenceAcknowledged,
    documentSectionAcknowledged: data.documentSectionAcknowledged,
  };

  // Only persist date/month-year fields when fully valid.
  if (
    validateDateValueParts(data.dob.day, data.dob.month, data.dob.year, {
      disallowFuture: true,
    }) === null
  ) {
    out.dob = data.dob;
  }
  if (
    validateOptionalDateParts(data.docExpiry.day, data.docExpiry.month, data.docExpiry.year, {
      requireFutureOrToday: true,
    }) === null
  ) {
    out.docExpiry = data.docExpiry;
  }
  if (
    validateOptionalMonthYear(data.residenceSince.month, data.residenceSince.year, {
      disallowFuture: true,
    }) === null
  ) {
    out.residenceSince = data.residenceSince;
  }
  try {
    localStorage.setItem(storageKey, JSON.stringify(out));
  } catch {
    // ignore storage write errors
  }
}

function applyDraft(prev: FormData, draft: DraftData | null): FormData {
  if (!draft) return prev;
  // Strip all document upload fields — they must always come from the API,
  // never from a stale localStorage draft.
  const {
    docUpload: _du, asylumDocType: _adt, asylumDocUpload: _adu,
    asylumOptionalExpanded: _aoe, denialResolutionUpload: _dru,
    denialResolutionDocumentId: _drdid,
    priorCriminalRecordUpload: _pcru, criminalRecordDocUpload: _crdu,
    countriesOfResidence: _cor, criminalCertificates: _cc,
    stayDocuments: _sd, familyDocuments: _fd, employmentDocuments: _ed,
    asylumDocuments: _ad, vulnerabilityReport: _vr, otherDocuments: _od,
    documentChecklistHints: _dch,
    caseNeedsAttentionMarked: _cna,
    caseNeedsAttentionMarkedAt: _cnat,
    ...safeDraft
  } = draft;
  const mergedDsa: Partial<Record<DocumentSectionAckKey, boolean>> = {
    ...prev.documentSectionAcknowledged,
    ...(draft.documentSectionAcknowledged &&
    typeof draft.documentSectionAcknowledged === "object" &&
    !Array.isArray(draft.documentSectionAcknowledged)
      ? draft.documentSectionAcknowledged
      : {}),
  };
  return {
    ...prev,
    ...draft,
    spouse: draft.spouse ? { ...prev.spouse, ...draft.spouse } : prev.spouse,
    children: Array.isArray(draft.children) ? (draft.children as ChildEntry[]) : prev.children,
    dependentParents: Array.isArray(draft.dependentParents)
      ? (draft.dependentParents as DependentParent[])
      : prev.dependentParents,
    arrivalDate: draft.arrivalDate ? { ...prev.arrivalDate, ...draft.arrivalDate } : prev.arrivalDate,
    oarCaseNumber: draft.oarCaseNumber !== undefined ? String(draft.oarCaseNumber) : prev.oarCaseNumber,
    asylumScenario:
      draft.asylumScenario !== undefined
        ? (draft.asylumScenario as FormData["asylumScenario"])
        : prev.asylumScenario,
    subRoute:
      draft.subRoute !== undefined ? (draft.subRoute as FormData["subRoute"]) : prev.subRoute,
    prorrogaType:
      draft.prorrogaType !== undefined
        ? (draft.prorrogaType as FormData["prorrogaType"])
        : prev.prorrogaType,
    irregularSubRoute:
      draft.irregularSubRoute !== undefined
        ? (draft.irregularSubRoute as FormData["irregularSubRoute"])
        : prev.irregularSubRoute,
    workType:
      draft.workType !== undefined ? (draft.workType as FormData["workType"]) : prev.workType,
    selfEmploymentActivity:
      draft.selfEmploymentActivity !== undefined
        ? String(draft.selfEmploymentActivity)
        : prev.selfEmploymentActivity,
    hasPassport:
      draft.hasPassport !== undefined ? (draft.hasPassport as FormData["hasPassport"]) : prev.hasPassport,
    asylumRoutingConfirmed:
      draft.asylumRoutingConfirmed !== undefined
        ? (draft.asylumRoutingConfirmed as FormData["asylumRoutingConfirmed"])
        : prev.asylumRoutingConfirmed,
    qualifyingGround:
      draft.qualifyingGround !== undefined
        ? (draft.qualifyingGround as FormData["qualifyingGround"])
        : prev.qualifyingGround,
    dob: draft.dob ? { ...prev.dob, ...draft.dob } : prev.dob,
    docExpiry: draft.docExpiry ? { ...prev.docExpiry, ...draft.docExpiry } : prev.docExpiry,
    residenceSince: draft.residenceSince
      ? { ...prev.residenceSince, ...draft.residenceSince }
      : prev.residenceSince,
    proofOfResidenceAcknowledged:
      mergedDsa.residence === true ||
      (typeof draft.proofOfResidenceAcknowledged === "boolean"
        ? draft.proofOfResidenceAcknowledged
        : prev.proofOfResidenceAcknowledged),
    documentSectionAcknowledged: mergedDsa,
    caseNeedsAttentionMarked: prev.caseNeedsAttentionMarked,
    caseNeedsAttentionMarkedAt: prev.caseNeedsAttentionMarkedAt,
    documentChecklistHints: prev.documentChecklistHints,
    identityPassportDocumentId: prev.identityPassportDocumentId,
  };
}

function buildServerAutosavePayload(data: FormData): Record<string, unknown> {
  const hasDependentsInSpain =
    data.hasChildren === "yes" || data.hasDependentParents === "yes"
      ? "yes"
      : data.hasChildren === "no" && data.hasDependentParents === "no"
        ? "no"
        : "";

  const toE164 = (countryCode: string, phoneRaw: string): string => {
    const raw = (phoneRaw || "").trim();
    if (!raw) return "";
    const compact = raw.replace(/[^\d+]/g, "");
    if (compact.startsWith("+")) return compact;
    const cc = (countryCode || "").replace(/[^\d+]/g, "") || "+34";
    return `${cc}${compact}`.replace(/\s+/g, "");
  };

  const applicantType =
    data.registrantType === "representative"
      ? "others"
      : data.registrantType === "individual"
        ? "self"
        : "";
  const normalizedDocType = String(data.docType || "").trim();
  const identityDocNumber =
    normalizedDocType === "passport"
      ? data.passportNumber
      : normalizedDocType === "nationalId"
        ? data.nationalIdNumber
        : normalizedDocType === "travelDoc"
          ? data.travelDocumentNumber
          : normalizedDocType === "cedulaInscripcion"
            ? data.registrationDocumentNumber
            : normalizedDocType === "other"
              ? data.otherDocumentNumber
              : "";
  const identityDocIssuingCountry =
    normalizedDocType && normalizedDocType !== "passport" ? data.nationalIdCountry : "";

  return {
    firstName: data.firstName,
    primerApellido: data.primerApellido,
    segundoApellido: data.segundoApellido,
    email: data.email.trim().toLowerCase(),
    phone: toE164(data.phoneCountryCode, data.phone),
    registrantType: data.registrantType,
    familyInterest: data.familyInterest,
    applicantType,
    repRelationship: data.repRelationship,
    applicantFirstName: data.applicantFirstName,
    applicantPrimerApellido: data.applicantPrimerApellido,
    applicantSegundoApellido: data.applicantSegundoApellido,
    applicantEmail: data.applicantEmail.trim().toLowerCase(),
    applicantPhone: data.applicantPhone.trim()
      ? toE164(data.applicantPhoneCountryCode, data.applicantPhone)
      : "",
    asylumRoutingConfirmed: data.asylumRoutingConfirmed,
    asylumApplicant: data.asylumApplicant,
    qualifyingGround: data.qualifyingGround,
    oarCaseNumber: data.oarCaseNumber,
    asylumScenario: data.asylumScenario,
    subRoute: data.subRoute,
    prorrogaType: data.prorrogaType,
    irregularSubRoute: data.irregularSubRoute,
    workHistory: data.workHistory,
    workType: data.workType,
    selfEmploymentActivity: data.selfEmploymentActivity,
    hasCriminalRecord: data.hasCriminalRecord,
    previousApplication: data.previousApplication,
    priorAsylumApplication: data.priorAsylumApplication,
    dob: data.dob,
    sex: data.sex,
    nationality: data.nationality,
    countryOfBirth: data.countryOfBirth,
    placeOfBirth: data.placeOfBirth,
    civilStatus: data.civilStatus,
    hasSpouse: data.hasSpouse,
    spouse: data.spouse,
    hasChildren: data.hasChildren,
    children: data.children,
    hasDependentParents: data.hasDependentParents,
    dependentParents: data.dependentParents,
    hasDependentsInSpain,
    fatherFirstName: data.fatherUnknown === "yes" ? "" : data.fatherFirstName,
    fatherApellido1: data.fatherUnknown === "yes" ? "" : data.fatherApellido1,
    fatherApellido2: data.fatherUnknown === "yes" ? "" : data.fatherApellido2,
    fatherUnknown: data.fatherUnknown,
    motherFirstName: data.motherUnknown === "yes" ? "" : data.motherFirstName,
    motherApellido1: data.motherUnknown === "yes" ? "" : data.motherApellido1,
    motherApellido2: data.motherUnknown === "yes" ? "" : data.motherApellido2,
    motherUnknown: data.motherUnknown,
    street: data.street,
    streetType: data.streetType,
    streetNumber: data.streetNumber,
    floorPiso: data.floorPiso,
    floorDoor: data.floorDoor,
    municipality: data.municipality,
    locality: data.locality,
    city: data.city,
    province: data.province,
    postalCode: data.postalCode,
    country: data.country,
    residenceSince: data.residenceSince,
    noReturnCommitment: data.noReturnCommitment,
    noHealthcareCoverage: data.noHealthcareCoverage,
    engagementConsent: data.engagementConsent,
    dataProcessingConsent: data.dataProcessingConsent,
    marketingConsent: data.marketingConsent,
    questionsRequested: data.questionsRequested,
    docType: data.docType,
    docTypeOther: data.docTypeOther,
    docFullName: data.docFullName,
    identityDocNumber,
    identityDocIssuingCountry,
    passportNumber: data.passportNumber,
    nationalIdNumber: data.nationalIdNumber,
    travelDocumentNumber: data.travelDocumentNumber,
    registrationDocumentNumber: data.registrationDocumentNumber,
    otherDocumentNumber: data.otherDocumentNumber,
    nationalIdCountry: data.nationalIdCountry,
    docExpiry: data.docExpiry,
    nie: data.nie,
    ...(data.hasValidPermit ? { hasValidPermit: data.hasValidPermit } : {}),
    ...(data.hasPassport ? { hasPassport: data.hasPassport } : {}),
  };
}

function buildServerAutosaveDelta(
  data: FormData,
  changedFields: Set<keyof FormData>
): Record<string, unknown> {
  const full = buildServerAutosavePayload(data);
  const include = new Set<string>();

  changedFields.forEach((field) => {
    switch (field) {
      case "phone":
      case "phoneCountryCode":
        include.add("phone");
        break;
      case "registrantType":
        include.add("registrantType");
        include.add("applicantType");
        break;
      case "applicantPhone":
      case "applicantPhoneCountryCode":
        include.add("applicantPhone");
        break;
      case "asylumRoutingConfirmed":
      case "asylumApplicant":
        include.add("asylumRoutingConfirmed");
        include.add("asylumApplicant");
        if (field === "asylumRoutingConfirmed" && data.asylumRoutingConfirmed === true) {
          include.add("qualifyingGround");
        }
        break;
      case "qualifyingGround":
        include.add("qualifyingGround");
        break;
      case "fatherUnknown":
        include.add("fatherUnknown");
        include.add("fatherFirstName");
        include.add("fatherApellido1");
        include.add("fatherApellido2");
        break;
      case "motherUnknown":
        include.add("motherUnknown");
        include.add("motherFirstName");
        include.add("motherApellido1");
        include.add("motherApellido2");
        break;
      case "docType":
        include.add("docType");
        include.add("identityDocNumber");
        include.add("identityDocIssuingCountry");
        include.add("passportNumber");
        include.add("nationalIdNumber");
        include.add("travelDocumentNumber");
        include.add("registrationDocumentNumber");
        include.add("otherDocumentNumber");
        include.add("nationalIdCountry");
        break;
      default:
        include.add(field);
        break;
    }
  });

  const out: Record<string, unknown> = {};
  include.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(full, key)) {
      out[key] = full[key];
    }
  });
  return out;
}

export function FormProvider({ children }: { children: ReactNode }) {
  const { lang } = useLang();
  const [data, setData] = useState<FormData>(initialData);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [currentStep, setCurrentStep] = useState(1);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [helpRequest, setHelpRequest] = useState<string | null>(null);
  const [caseSessionId, setCaseSessionId] = useState<string | null>(null);
  const [draftStorageKey, setDraftStorageKey] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serverAutosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestServerAutosaveReq = useRef(0);
  const pendingAutosaveFields = useRef<Set<keyof FormData>>(new Set());
  const draftHydratedRef = useRef(false);
  const activeDraftStorageKeyRef = useRef<string | null>(null);
  const lastChinesePopupAt = useRef(0);

  const triggerSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(() => {
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2000);
    }, 600);
  }, []);

  const update = useCallback(
    (field: keyof FormData, value: unknown) => {
      if (field === "caseNeedsAttentionMarked") {
        setData((prev) => ({ ...prev, caseNeedsAttentionMarked: value === true }));
        return;
      }
      if (field === "caseNeedsAttentionMarkedAt") {
        setData((prev) => ({
          ...prev,
          caseNeedsAttentionMarkedAt:
            typeof value === "string" && value.trim()
              ? value.trim()
              : prev.caseNeedsAttentionMarkedAt ?? "",
        }));
        return;
      }
      if (field === "proofOfResidenceAcknowledged") {
        setData((prev) => ({ ...prev, proofOfResidenceAcknowledged: value === true }));
        return;
      }
      if (field === "documentSectionAcknowledged") {
        setData((prev) => {
          const patch =
            typeof value === "object" && value !== null && !Array.isArray(value)
              ? (value as Partial<Record<DocumentSectionAckKey, boolean>>)
              : {};
          const merged = { ...prev.documentSectionAcknowledged, ...patch };

          let nextProofOfResidenceAck = prev.proofOfResidenceAcknowledged;
          if (Object.prototype.hasOwnProperty.call(patch, "residence")) {
            nextProofOfResidenceAck = patch.residence === true;
          } else if (merged.residence === true) {
            nextProofOfResidenceAck = true;
          }

          return {
            ...prev,
            documentSectionAcknowledged: merged,
            proofOfResidenceAcknowledged: nextProofOfResidenceAck,
          };
        });
        return;
      }
      if (
        CHINESE_BLOCKED_INPUT_FIELDS.has(field) &&
        typeof value === "string" &&
        containsChineseChars(value)
      ) {
        const now = Date.now();
        if (now - lastChinesePopupAt.current > 1400) {
          showPopupMessage(getChineseInputBlockedMessage(lang), "error");
          lastChinesePopupAt.current = now;
        }
        return;
      }
      setData((prev) => {
        const normalizedValue =
          field === "fatherFirstName" || field === "motherFirstName"
            ? normalizeParentFirstNameValue(value)
            : field === "placeOfBirth"
              ? normalizeTitleCaseWordStarts(value)
              : value;
        const next = { ...prev, [field]: normalizedValue };

        pendingAutosaveFields.current.add(field);
        if (serverAutosaveTimer.current) clearTimeout(serverAutosaveTimer.current);
        serverAutosaveTimer.current = setTimeout(async () => {
          const reqId = ++latestServerAutosaveReq.current;
          const fieldsToSave = new Set(pendingAutosaveFields.current);
          pendingAutosaveFields.current.clear();
          try {
            if (fieldsToSave.size > 0) {
              await mvpApi.applicantAutosave(buildServerAutosaveDelta(next, fieldsToSave));
            }
          } catch {
            // best effort: keep local draft regardless of network errors
          } finally {
            if (latestServerAutosaveReq.current === reqId) {
              serverAutosaveTimer.current = null;
            }
          }
        }, 800);
        return next;
      });
      triggerSave();
    },
    [lang, triggerSave]
  );

  const goNext = useCallback(() => {
    if (currentStep <= TOTAL_STEPS) setCurrentStep((s) => s + 1);
  }, [currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= TOTAL_STEPS) setCurrentStep(step);
  }, []);

  const requestHelp = useCallback((field: string) => setHelpRequest(field), []);
  const clearHelpRequest = useCallback(() => setHelpRequest(null), []);

  const ensureCaseSession = useCallback(async (): Promise<string | null> => {
    if (caseSessionId) return caseSessionId;
    try {
      const res = await mvpApi.startSession({ language: "es" });
      const sid = String(res?.data?.session_id ?? res?.data?.sessionId ?? "").trim();
      if (sid) {
        setCaseSessionId(sid);
        return sid;
      }
    } catch {
      // caller shows error
    }
    return null;
  }, [caseSessionId]);

  useEffect(() => {
    let mounted = true;

    async function hydrateFromMe() {
      try {
        const res = await mvpApi.getMe();
        const user = res?.data?.user || res?.data || {};

        const first = String(user.first_name || user.firstName || "").trim();
        const last = String(user.last_name || user.lastName || "").trim();
        const secondSurnameFromApi = String(user.second_surname || user.secondSurname || "").trim();
        const fallbackName = String(user.full_name || user.fullName || "").trim();
        const mergedName = [first, last].filter(Boolean).join(" ").trim() || fallbackName;

        const emailValue = String(user.email || "").trim();
        const phoneValue = String(user.phone || "").trim();
        const dialCodeValue = String(user.phone_country_code || user.phoneCountryCode || "").trim();
        const applicantTypeValue = String(user.applicant_type || user.applicantType || "").trim().toLowerCase();
        const userIdValue = String(user.id ?? "").trim();
        const registrantTypeFromApi: FormData["registrantType"] =
          applicantTypeValue === "others"
            ? "representative"
            : applicantTypeValue === "self"
              ? "individual"
              : "";

        if (!mounted) return;
        const nextDraftStorageKey = userIdValue
          ? `${APPLICANT_DRAFT_STORAGE_KEY_PREFIX}:${userIdValue}`
          : null;
        if (activeDraftStorageKeyRef.current !== nextDraftStorageKey) {
          draftHydratedRef.current = false;
          activeDraftStorageKeyRef.current = nextDraftStorageKey;
        }
        setDraftStorageKey(nextDraftStorageKey);
        try {
          localStorage.removeItem(APPLICANT_DRAFT_STORAGE_KEY_LEGACY);
        } catch {
          // ignore storage cleanup errors
        }

        setData((prev) => {
          const arrivalRaw = String(user.arrival_date || user.arrivalDate || "").trim();
          let arrivalDateValue = prev.arrivalDate;
          if (arrivalRaw) {
            const dt = new Date(arrivalRaw);
            if (!Number.isNaN(dt.getTime())) {
              arrivalDateValue = {
                day: String(dt.getUTCDate()).padStart(2, "0"),
                month: String(dt.getUTCMonth() + 1).padStart(2, "0"),
                year: String(dt.getUTCFullYear()),
              };
            }
          }
          const docExpiryRaw = String(user.identity_doc_expiry_at || user.identityDocExpiryAt || "").trim();
          let docExpiryValue = prev.docExpiry;
          if (docExpiryRaw) {
            const dt = new Date(docExpiryRaw);
            if (!Number.isNaN(dt.getTime())) {
              docExpiryValue = {
                day: String(dt.getUTCDate()).padStart(2, "0"),
                month: String(dt.getUTCMonth() + 1).padStart(2, "0"),
                year: String(dt.getUTCFullYear()),
              };
            }
          }
          const residenceSinceRaw = String(user.residence_since || user.residenceSince || "").trim();
          let residenceSinceValue = prev.residenceSince;
          if (residenceSinceRaw) {
            const dt = new Date(residenceSinceRaw);
            if (!Number.isNaN(dt.getTime())) {
              residenceSinceValue = {
                month: String(dt.getUTCMonth() + 1).padStart(2, "0"),
                year: String(dt.getUTCFullYear()),
              };
            }
          }
          const yn = (v: unknown): "yes" | "no" | "" => {
            if (v === true) return "yes";
            if (v === false) return "no";
            return "";
          };
          const splitName = mergedName ? splitFullName(mergedName) : null;
          const surnameParts = splitSurnamesFromLastName(last);
          const resolvedFirstName = first || (splitName ? splitName.first : "");
          const resolvedPrimerApellido = surnameParts.primer || (splitName ? splitName.primer : "");
          const resolvedSegundoApellido =
            secondSurnameFromApi || surnameParts.segundo || (splitName ? splitName.segundo : "");
          const resolvedFullName = composeFullNameParts(
            resolvedFirstName,
            resolvedPrimerApellido,
            resolvedSegundoApellido
          );
          const repPhoneSplit = splitDialNationalFromStored(
            phoneValue,
            dialCodeValue || prev.phoneCountryCode || "+34"
          );
          const phoneWithPrefix = (phoneValue || prev.phone).trim();
          const dialCodeForDisplay = (
            repPhoneSplit.dial ||
            dialCodeValue ||
            prev.phoneCountryCode ||
            "+34"
          ).trim();
          const phoneConfirmedValue = phoneWithPrefix.startsWith("+")
            ? phoneWithPrefix
            : `${dialCodeForDisplay} ${phoneWithPrefix}`.trim();
          const rawApplicantPhone = String(user.applicant_phone ?? user.applicantPhone ?? "").trim();
          const apiApplicantDial = String(
            user.applicant_phone_country_code ?? user.applicantPhoneCountryCode ?? ""
          ).trim();
          const applicantSplit = splitDialNationalFromStored(
            rawApplicantPhone,
            apiApplicantDial || dialCodeForDisplay || "+34"
          );
          return {
            ...prev,
            fullName: resolvedFullName || mergedName || prev.fullName,
            firstName: resolvedFirstName || prev.firstName,
            primerApellido: resolvedPrimerApellido || prev.primerApellido,
            segundoApellido: resolvedSegundoApellido || prev.segundoApellido,
            email: emailValue || prev.email,
            emailConfirmed: emailValue || prev.emailConfirmed,
            phone: !phoneValue
              ? prev.phone
              : phoneValue.startsWith("+")
                ? repPhoneSplit.national
                : phoneValue,
            phoneCountryCode: !phoneValue
              ? prev.phoneCountryCode
              : phoneValue.startsWith("+")
                ? repPhoneSplit.dial
                : dialCodeValue || prev.phoneCountryCode,
            registrantType: registrantTypeFromApi || prev.registrantType,
            familyInterest:
              user.family_interest === true ||
              user.interested_in_family === true ||
              prev.familyInterest,
            repPhoneOtpVerified:
              registrantTypeFromApi === "representative"
                ? Boolean(
                    user.phone_verified === true ||
                      user.phoneVerified === true ||
                      !!(String(user.oauth_provider ?? user.oauthProvider ?? "").trim())
                  )
                : true,
            repRelationship:
              String(user.rep_relationship_to_applicant ?? user.repRelationshipToApplicant ?? "").trim() ||
              prev.repRelationship,
            applicantFirstName:
              String(user.applicant_first_name ?? user.applicantFirstName ?? "").trim() ||
              prev.applicantFirstName,
            applicantPrimerApellido:
              String(user.applicant_primer_apellido ?? user.applicantPrimerApellido ?? "").trim() ||
              prev.applicantPrimerApellido,
            applicantSegundoApellido:
              String(user.applicant_segundo_apellido ?? user.applicantSegundoApellido ?? "").trim() ||
              prev.applicantSegundoApellido,
            applicantEmail:
              String(user.applicant_email ?? user.applicantEmail ?? "").trim() || prev.applicantEmail,
            applicantPhone: !rawApplicantPhone
              ? prev.applicantPhone
              : rawApplicantPhone.startsWith("+")
                ? applicantSplit.national
                : rawApplicantPhone,
            applicantPhoneCountryCode: !rawApplicantPhone
              ? prev.applicantPhoneCountryCode
              : rawApplicantPhone.startsWith("+")
                ? applicantSplit.dial
                : apiApplicantDial ||
                  dialCodeForDisplay ||
                  prev.applicantPhoneCountryCode ||
                  "+34",
            arrivalDate: arrivalDateValue,
            hasValidPermit: yn(user.has_valid_permit),
            asylumRoutingConfirmed: (() => {
              const explicit = yn(
                (user as { asylum_routing_confirmed?: boolean | null; asylumRoutingConfirmed?: boolean | null })
                  .asylum_routing_confirmed ??
                  (user as { asylumRoutingConfirmed?: boolean | null }).asylumRoutingConfirmed
              );
              if (explicit === "yes") return true;
              if (explicit === "no") return false;
              const derived = yn(user.asylum_applicant);
              if (derived === "yes") return true;
              if (derived === "no") return false;
              return prev.asylumRoutingConfirmed;
            })(),
            asylumApplicant: yn(user.asylum_applicant),
            qualifyingGround: (() => {
              const raw = String(
                (user as { qualifying_ground?: string | null; qualifyingGround?: string | null }).qualifying_ground ??
                  (user as { qualifyingGround?: string | null }).qualifyingGround ??
                  ""
              ).trim();
              if (raw === "work" || raw === "family" || raw === "vulnerability") {
                return raw as FormData["qualifyingGround"];
              }
              return prev.qualifyingGround;
            })(),
            oarCaseNumber:
              String(user.oar_case_number ?? user.oarCaseNumber ?? "").trim() || prev.oarCaseNumber,
            asylumScenario: (() => {
              const raw = String(
                (user as { asylum_scenario?: string | null; asylumScenario?: string | null }).asylum_scenario ??
                  (user as { asylumScenario?: string | null }).asylumScenario ??
                  ""
              ).trim();
              if (["1", "2", "3", "4", "5", "6"].includes(raw)) {
                return raw as FormData["asylumScenario"];
              }
              return prev.asylumScenario;
            })(),
            subRoute: (() => {
              const raw = String(
                (user as { sub_route?: string | null; subRoute?: string | null }).sub_route ??
                  (user as { subRoute?: string | null }).subRoute ??
                  ""
              ).trim();
              if (["7A", "7B", "7C", "8", "9", "10", "11", "12"].includes(raw)) {
                return raw as FormData["subRoute"];
              }
              return prev.subRoute;
            })(),
            prorrogaType: (() => {
              const raw = String(
                (user as { prorroga_type?: string | null; prorrogaType?: string | null }).prorroga_type ??
                  (user as { prorrogaType?: string | null }).prorrogaType ??
                  ""
              ).trim();
              if (["jobSearch", "integrationReport", "illness", "disability", "retirement"].includes(raw)) {
                return raw as FormData["prorrogaType"];
              }
              return prev.prorrogaType;
            })(),
            irregularSubRoute: (() => {
              const r = String(user.irregular_sub_route ?? user.irregularSubRoute ?? "")
                .trim()
                .toLowerCase();
              if (r === "work" || r === "family" || r === "vulnerability") {
                return r as FormData["irregularSubRoute"];
              }
              const mappedFromSubRoute = String(
                (user as { sub_route?: string | null; subRoute?: string | null }).sub_route ??
                  (user as { subRoute?: string | null }).subRoute ??
                  ""
              ).trim();
              if (mappedFromSubRoute === "7A") return "work";
              if (mappedFromSubRoute === "7B") return "family";
              if (mappedFromSubRoute === "7C") return "vulnerability";
              return prev.irregularSubRoute;
            })(),
            workHistory: (() => {
              const raw = String(
                (user as { work_history?: string | null; workHistory?: string | null }).work_history ??
                  (user as { workHistory?: string | null }).workHistory ??
                  ""
              )
                .trim()
                .toLowerCase();
              if (raw === "worked" || raw === "current" || raw === "offer") {
                return raw as FormData["workHistory"];
              }
              return prev.workHistory;
            })(),
            workType: (() => {
              const raw = String(
                (user as { work_type?: string | null; workType?: string | null }).work_type ??
                  (user as { workType?: string | null }).workType ??
                  ""
              ).trim();
              const norm = raw.replace(/_/g, "").toLowerCase();
              if (norm === "employed") return "employed" as FormData["workType"];
              if (norm === "selfemployed") return "selfemployed" as FormData["workType"];
              return prev.workType;
            })(),
            selfEmploymentActivity:
              String(
                (user as {
                  self_employment_activity?: string | null;
                  selfEmploymentActivity?: string | null;
                }).self_employment_activity ??
                  (user as { selfEmploymentActivity?: string | null }).selfEmploymentActivity ??
                  ""
              ).trim() || prev.selfEmploymentActivity,
            hasCriminalRecord: yn(user.has_criminal_record),
            previousApplication: yn(user.previous_application),
            hasPassport: yn((user as { has_passport?: boolean | null }).has_passport),
            docType: (user.identity_doc_type as FormData["docType"]) || prev.docType,
            docTypeOther: String(user.identity_doc_type_other || "").trim() || prev.docTypeOther,
            docFullName: String(user.identity_doc_full_name || "").trim() || prev.docFullName,
            passportNumber:
              String(
                (user as { passport_number?: string | null }).passport_number ??
                  ((((user.identity_doc_type as FormData["docType"]) || prev.docType) === "passport"
                    ? user.identity_doc_number
                    : "") as string | null | undefined) ??
                  ""
              ).trim() || prev.passportNumber,
            nationalIdNumber:
              String(
                (user as { national_id_document_number?: string | null }).national_id_document_number ??
                  ((((user.identity_doc_type as FormData["docType"]) || prev.docType) === "nationalId"
                    ? user.identity_doc_number
                    : "") as string | null | undefined) ??
                  ""
              ).trim() || prev.nationalIdNumber,
            travelDocumentNumber:
              String(
                (user as { travel_document_number?: string | null }).travel_document_number ??
                  ((((user.identity_doc_type as FormData["docType"]) || prev.docType) === "travelDoc"
                    ? user.identity_doc_number
                    : "") as string | null | undefined) ??
                  ""
              ).trim() || prev.travelDocumentNumber,
            registrationDocumentNumber:
              String(
                (user as { registration_document_number?: string | null }).registration_document_number ??
                  ((((user.identity_doc_type as FormData["docType"]) || prev.docType) === "cedulaInscripcion"
                    ? user.identity_doc_number
                    : "") as string | null | undefined) ??
                  ""
              ).trim() || prev.registrationDocumentNumber,
            otherDocumentNumber:
              String(
                (user as { other_document_number?: string | null }).other_document_number ??
                  ((((user.identity_doc_type as FormData["docType"]) || prev.docType) === "other"
                    ? user.identity_doc_number
                    : "") as string | null | undefined) ??
                  ""
              ).trim() || prev.otherDocumentNumber,
            nationalIdCountry:
              String((user as { identity_doc_issuing_country?: string | null }).identity_doc_issuing_country || "")
                .trim() || prev.nationalIdCountry,
            docExpiry: docExpiryValue,
            nie: String(user.nie || "").trim() || prev.nie,
            dob: (() => {
              const raw = String(
                user.fecha_nacimiento ||
                user.date_of_birth ||
                user.dateOfBirth ||
                ""
              ).trim();
              if (!raw) return prev.dob;
              const parsed = toDateValueFromApi(raw);
              return parsed.year ? parsed : prev.dob;
            })(),
            sex: (String(user.sex || "").trim() as FormData["sex"]) || prev.sex,
            nationality: String(user.nationality || "").trim() || prev.nationality,
            countryOfBirth: String(user.country_of_birth || user.countryOfBirth || "").trim() || prev.countryOfBirth,
            placeOfBirth: String(user.place_of_birth || user.placeOfBirth || "").trim() || prev.placeOfBirth,
            civilStatus:
              (normalizeCivilStatusSlug(user.civil_status || (user as { estado_civil?: string }).estado_civil) as FormData["civilStatus"]) ||
              prev.civilStatus,
            hasSpouse: yn((user as { has_spouse?: boolean | null }).has_spouse) || prev.hasSpouse,
            spouse: (() => {
              const hasSpouse = yn((user as { has_spouse?: boolean | null }).has_spouse);
              if (hasSpouse === "no") return initialData.spouse;
              if (Object.prototype.hasOwnProperty.call(user, "spouse")) {
                return normalizeSpouseFromApi((user as { spouse?: unknown }).spouse, prev.spouse);
              }
              return prev.spouse;
            })(),
            hasChildren:
              yn((user as { has_children?: boolean | null }).has_children) ||
              prev.hasChildren ||
              (yn(user.has_dependents_in_spain) === "no" ? "no" : prev.hasChildren),
            children: (() => {
              const hasChildren = yn((user as { has_children?: boolean | null }).has_children);
              if (hasChildren === "no") return [];
              if (Object.prototype.hasOwnProperty.call(user, "children")) {
                return normalizeChildrenFromApi((user as { children?: unknown }).children);
              }
              return prev.children;
            })(),
            hasDependentParents:
              yn((user as { has_dependent_parents?: boolean | null }).has_dependent_parents) ||
              prev.hasDependentParents ||
              (yn(user.has_dependents_in_spain) === "no" ? "no" : prev.hasDependentParents),
            dependentParents: (() => {
              const hasParents = yn((user as { has_dependent_parents?: boolean | null }).has_dependent_parents);
              if (hasParents === "no") return [];
              if (Object.prototype.hasOwnProperty.call(user, "dependent_parents")) {
                return normalizeDependentParentsFromApi((user as { dependent_parents?: unknown }).dependent_parents);
              }
              return prev.dependentParents;
            })(),
            hasDependentsInSpain:
              yn(user.has_dependents_in_spain) || prev.hasDependentsInSpain,
            fatherUnknown:
              typeof user.father_not_known === "boolean"
                ? (user.father_not_known ? "yes" : "")
                : prev.fatherUnknown,
            motherUnknown:
              typeof user.mother_not_known === "boolean"
                ? (user.mother_not_known ? "yes" : "")
                : prev.motherUnknown,
            fatherFirstName:
              typeof user.father_not_known === "boolean" && user.father_not_known
                ? ""
                : String(user.father_first_name || "").trim() || prev.fatherFirstName,
            fatherApellido1:
              typeof user.father_not_known === "boolean" && user.father_not_known
                ? ""
                : String(
                    (user as { father_primer_apellido?: string }).father_primer_apellido || ""
                  ).trim() || prev.fatherApellido1,
            fatherApellido2:
              typeof user.father_not_known === "boolean" && user.father_not_known
                ? ""
                : String(
                    (user as { father_segundo_apellido?: string }).father_segundo_apellido || ""
                  ).trim() || prev.fatherApellido2,
            motherFirstName:
              typeof user.mother_not_known === "boolean" && user.mother_not_known
                ? ""
                : String(user.mother_first_name || "").trim() || prev.motherFirstName,
            motherApellido1:
              typeof user.mother_not_known === "boolean" && user.mother_not_known
                ? ""
                : String(
                    (user as { mother_primer_apellido?: string }).mother_primer_apellido || ""
                  ).trim() || prev.motherApellido1,
            motherApellido2:
              typeof user.mother_not_known === "boolean" && user.mother_not_known
                ? ""
                : String(
                    (user as { mother_segundo_apellido?: string }).mother_segundo_apellido || ""
                  ).trim() || prev.motherApellido2,
            street:
              pickMeAddressString(user as Record<string, unknown>, "street_name", "street") || prev.street,
            streetType:
              pickMeAddressString(user as Record<string, unknown>, "street_type", "streetType") ||
              prev.streetType,
            streetNumber:
              pickMeAddressString(user as Record<string, unknown>, "street_number", "streetNumber") ||
              prev.streetNumber,
            ...(() => {
              let fp = String(
                user.floor_piso ?? (user as { floorPiso?: string }).floorPiso ?? ""
              ).trim();
              let fd = String(
                user.floor_door ?? (user as { floorDoor?: string }).floorDoor ?? ""
              ).trim();
              const hasSplitFloor =
                user.floor_piso !== undefined &&
                user.floor_piso !== null &&
                String(user.floor_piso).trim() !== "";
              if (!fp && fd && !hasSplitFloor) {
                const parsed = parseStoredFloorDoor(fd);
                fp = parsed.piso;
                fd = parsed.door;
              }
              const apiHadAddressFloor =
                user.floor_piso != null ||
                user.floor_door != null ||
                (user as { floorPiso?: string | null }).floorPiso != null ||
                (user as { floorDoor?: string | null }).floorDoor != null;
              return {
                floorPiso: fp || prev.floorPiso,
                floorDoor: apiHadAddressFloor ? fd : fd || prev.floorDoor,
              };
            })(),
            municipality:
              pickMeAddressString(user as Record<string, unknown>, "municipality", "municipality") ||
              prev.municipality,
            locality:
              pickMeAddressString(user as Record<string, unknown>, "locality", "locality") ||
              pickMeAddressString(user as Record<string, unknown>, "city_locality", "city") ||
              prev.locality,
            city:
              pickMeAddressString(user as Record<string, unknown>, "locality", "locality") ||
              pickMeAddressString(user as Record<string, unknown>, "city_locality", "city") ||
              prev.city,
            province: String(user.province ?? "").trim() || prev.province,
            postalCode:
              pickMeAddressString(user as Record<string, unknown>, "postal_code", "postalCode") || prev.postalCode,
            country: String(user.country ?? "").trim() || prev.country,
            residenceSince: residenceSinceValue,
            noReturnCommitment:
              typeof (user as { no_return_commitment?: boolean }).no_return_commitment === "boolean"
                ? Boolean((user as { no_return_commitment?: boolean }).no_return_commitment)
                : prev.noReturnCommitment,
            noHealthcareCoverage:
              typeof (user as { no_healthcare_coverage?: boolean }).no_healthcare_coverage === "boolean"
                ? Boolean((user as { no_healthcare_coverage?: boolean }).no_healthcare_coverage)
                : prev.noHealthcareCoverage,
            engagementConsent:
              typeof user.engagement_consent === "boolean"
                ? user.engagement_consent
                : prev.engagementConsent,
            dataProcessingConsent:
              typeof user.data_processing_consent === "boolean"
                ? user.data_processing_consent
                : prev.dataProcessingConsent,
            marketingConsent:
              typeof user.marketing_consent === "boolean"
                ? user.marketing_consent
                : prev.marketingConsent,
            questionsRequested:
              typeof (user as { questions_requested?: boolean }).questions_requested === "boolean"
                ? Boolean((user as { questions_requested?: boolean }).questions_requested)
                : prev.questionsRequested,
            signature:
              String(user.engagement_signature_data_url || "").trim() || prev.signature,
            phoneConfirmed:
              phoneValue || dialCodeValue
                ? phoneConfirmedValue
                : prev.phoneConfirmed,
          };
        });

        if (!mounted) return;
        let priorNorm: FormData["priorAsylumApplication"] = "";
        try {
          const startRes = await mvpApi.startSession({ language: "es" });
          const sid = String(
            startRes?.data?.session_id ?? startRes?.data?.sessionId ?? ""
          ).trim();
          if (!sid) return;
          if (!mounted) return;
          setCaseSessionId(sid);
          const rawPrior = startRes?.data?.prior_asylum_application;
          priorNorm =
            rawPrior === "before" || rawPrior === "after" || rawPrior === "never" ? rawPrior : "";
          const prefilled = (startRes?.data?.prefilled_fields ?? {}) as Record<string, unknown>;
          const mongoPorAck =
            prefilled.proof_of_residence_acknowledged === true ||
            prefilled.proofOfResidenceAcknowledged === true;
          const rawDsa = prefilled.document_section_acknowledged;
          const dsaFromMongo: Partial<Record<DocumentSectionAckKey, boolean>> = {};
          if (rawDsa && typeof rawDsa === "object" && !Array.isArray(rawDsa)) {
            for (const [k, v] of Object.entries(rawDsa as Record<string, unknown>)) {
              if (v === true) {
                dsaFromMongo[k as DocumentSectionAckKey] = true;
              }
            }
          }
          const sumRes = await mvpApi.getSessionDocumentsSummary(sid);
          if (!mounted) return;
          const rawDocs = sumRes?.data?.documents ?? [];
          const hints = deriveDocumentChecklistHints(rawDocs);
          const uploadNames = deriveUploadFilenamesFromSessionSummary(rawDocs);
          setData((prev) => {
            const criminalMerged = mergeCriminalStateFromSessionSummary(
              rawDocs,
              prev.countriesOfResidence,
              prev.criminalCertificates
            );
            const familyMerged = mergeFamilyStateFromSessionSummary(
              rawDocs,
              prev.familyDocuments
            );
            const stayMerged = mergeStayStateFromSessionSummary(
              rawDocs,
              prev.stayDocuments
            );
            const employmentMerged = mergeEmploymentStateFromSessionSummary(
              rawDocs,
              prev.employmentDocuments
            );
            const workTypeFromDocs = inferWorkTypeFromUploadedEmploymentDocs(employmentMerged);
            const mergedWorkType =
              prev.workType === "employed" || prev.workType === "selfemployed"
                ? prev.workType
                : workTypeFromDocs || prev.workType;
            const employmentActivityFromDocs = rawDocs
              .map((doc) => String(doc.self_employment_activity ?? "").trim())
              .find(Boolean);
            const asylumMerged = mergeAsylumStateFromSessionSummary(
              rawDocs,
              prev.asylumDocuments
            );
            const otherMerged = mergeOtherDocumentsFromSessionSummary(rawDocs, prev.otherDocuments);
            const vulnFromSummary = mergeVulnerabilityReportFromSessionSummary(rawDocs);
            const needsAttentionFlag =
              sumRes?.data?.needs_attention === true ||
              sumRes?.data?.needsAttention === true;
            const needsAttentionMarkedAt = String(
              sumRes?.data?.needs_attention_marked_at ?? sumRes?.data?.needsAttentionMarkedAt ?? ""
            ).trim();
            const next: FormData = {
              ...prev,
              documentChecklistHints: hints,
              caseNeedsAttentionMarked: needsAttentionFlag,
              caseNeedsAttentionMarkedAt: needsAttentionFlag
                ? (needsAttentionMarkedAt || prev.caseNeedsAttentionMarkedAt || "")
                : "",
              documentSectionAcknowledged: {
                ...prev.documentSectionAcknowledged,
                ...dsaFromMongo,
              },
              proofOfResidenceAcknowledged:
                mongoPorAck ||
                dsaFromMongo.residence === true ||
                prev.proofOfResidenceAcknowledged,
              priorAsylumApplication: priorNorm,
              countriesOfResidence: criminalMerged.countriesOfResidence,
              criminalCertificates: criminalMerged.criminalCertificates,
              stayDocuments: stayMerged,
              familyDocuments: familyMerged,
              employmentDocuments: employmentMerged,
              workType: mergedWorkType,
              selfEmploymentActivity:
                employmentActivityFromDocs || prev.selfEmploymentActivity,
              asylumDocuments: asylumMerged,
              otherDocuments: otherMerged,
              vulnerabilityReport: vulnFromSummary
                ? {
                    filename: vulnFromSummary.filename,
                    fileSize: vulnFromSummary.fileSize || "",
                    documentId: vulnFromSummary.documentId,
                  }
                : prev.vulnerabilityReport,
            };
            if (uploadNames.identityUploads) {
              next.identityUploads = {
                ...prev.identityUploads,
                ...uploadNames.identityUploads,
              };
              const currentIdentityType =
                (next.docType || prev.docType || "") as keyof FormData["identityUploads"] | "";
              next.docUpload = currentIdentityType
                ? next.identityUploads[currentIdentityType] || ""
                : "";
            }
            if (uploadNames.asylumDocUpload) next.asylumDocUpload = uploadNames.asylumDocUpload;
            if (uploadNames.denialResolutionUpload) next.denialResolutionUpload = uploadNames.denialResolutionUpload;
            if (uploadNames.denialResolutionDocumentId !== undefined)
              next.denialResolutionDocumentId = uploadNames.denialResolutionDocumentId ?? "";
            if (uploadNames.priorCriminalRecordUpload)
              next.priorCriminalRecordUpload = uploadNames.priorCriminalRecordUpload;
            if (uploadNames.criminalRecordDocUpload)
              next.criminalRecordDocUpload = uploadNames.criminalRecordDocUpload;
            next.identityPassportDocumentId = uploadNames.identityPassportDocumentId;
            return next;
          });
        } catch {
          if (mounted) {
            setData((prev) => ({
              ...prev,
              documentChecklistHints: undefined,
              caseNeedsAttentionMarked: prev.caseNeedsAttentionMarked,
              caseNeedsAttentionMarkedAt: prev.caseNeedsAttentionMarkedAt,
              priorAsylumApplication: priorNorm || prev.priorAsylumApplication,
            }));
          }
        }
      } catch {
        // Keep local empty defaults when /me is unavailable.
      }
    }

    hydrateFromMe();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!draftStorageKey) return;
    if (!draftHydratedRef.current) {
      const draft = readApplicantDraft(draftStorageKey);
      if (draft) {
        setData((prev) => applyDraft(prev, draft));
      }
      draftHydratedRef.current = true;
      return;
    }
    writeApplicantDraft(draftStorageKey, data);
  }, [data, draftStorageKey]);

  useEffect(
    () => () => {
      if (serverAutosaveTimer.current) clearTimeout(serverAutosaveTimer.current);
    },
    []
  );

  return (
    <FormContext.Provider value={{
      data, update, saveState,
      currentStep, totalSteps: TOTAL_STEPS,
      goNext, goBack, goToStep,
      consentAccepted, setConsentAccepted,
      helpRequest, requestHelp, clearHelpRequest,
      caseSessionId, ensureCaseSession,
    }}>
      {children}
    </FormContext.Provider>
  );
}

export function useForm() {
  const ctx = useContext(FormContext);
  if (!ctx) throw new Error("useForm must be inside FormProvider");
  return ctx;
}
